import React, { useState, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { TrendingUp, TrendingDown, BarChart3, RefreshCw, Filter, AlertTriangle, Mail } from 'lucide-react';
import api from './api/axios';
import SubscriptionModal from './SubscriptionModal';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const BalanceAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [selectedDays, setSelectedDays] = useState(30);
  const [ioltaThreshold, setIoltaThreshold] = useState(20000);
  const [checkingThreshold, setCheckingThreshold] = useState(5000);
  const [customIoltaThreshold, setCustomIoltaThreshold] = useState('');
  const [customCheckingThreshold, setCustomCheckingThreshold] = useState('');
  const [useCustomIoltaThreshold, setUseCustomIoltaThreshold] = useState(false);
  const [useCustomCheckingThreshold, setUseCustomCheckingThreshold] = useState(false);
  const [showIolta, setShowIolta] = useState(true);
  const [showChecking, setShowChecking] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  // Fetch daily balance changes data
  const fetchAnalyticsData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append('days', selectedDays.toString());
      const types = [];
      if (showIolta) types.push('iolta');
      if (showChecking) types.push('checking');

      if (types.length === 0) {
        setAnalyticsData({ law_firms: [] });
        return;
      }

      params.append('account_types', types.join(','));

      const response = await api.get(`/api/balance/accounts-by-law-firm?${params}`);
      setAnalyticsData(response.data);
    } catch (err) {
      console.error('Error fetching daily balance changes:', err);
      setError('Failed to load balance analytics. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedDays, showIolta, showChecking]);

  // Handle refresh
  const handleRefresh = async () => {
    if (refreshing) return;
    
    setRefreshing(true);
    try {
      await fetchAnalyticsData();
    } finally {
      setRefreshing(false);
    }
  };

  // Handle threshold changes
  const handleIoltaThresholdChange = (value) => {
    if (value === 'custom') {
      setUseCustomIoltaThreshold(true);
      if (customIoltaThreshold) {
        const numValue = parseFloat(customIoltaThreshold);
        if (!isNaN(numValue) && numValue >= 0) setIoltaThreshold(numValue);
      }
    } else {
      setUseCustomIoltaThreshold(false);
      setIoltaThreshold(parseInt(value));
    }
  };

  const handleCheckingThresholdChange = (value) => {
    if (value === 'custom') {
      setUseCustomCheckingThreshold(true);
      if (customCheckingThreshold) {
        const numValue = parseFloat(customCheckingThreshold);
        if (!isNaN(numValue) && numValue >= 0) setCheckingThreshold(numValue);
      }
    } else {
      setUseCustomCheckingThreshold(false);
      setCheckingThreshold(parseInt(value));
    }
  };

  const applyCustomIoltaThreshold = () => {
    if (useCustomIoltaThreshold && customIoltaThreshold) {
      const numValue = parseFloat(customIoltaThreshold);
      if (!isNaN(numValue) && numValue >= 0) setIoltaThreshold(numValue);
    }
  };

  const applyCustomCheckingThreshold = () => {
    if (useCustomCheckingThreshold && customCheckingThreshold) {
      const numValue = parseFloat(customCheckingThreshold);
      if (!isNaN(numValue) && numValue >= 0) setCheckingThreshold(numValue);
    }
  };

  // Load data on component mount and when filters change
  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    // Parse as local date to avoid timezone issues
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Get firms with significant balance increases that need monitoring
  const getHighBalanceIncreaseEvents = () => {
    if (!analyticsData?.law_firms) return [];

    const events = [];

    analyticsData.law_firms.forEach(firm => {
      const firmName = firm.law_firm;
      const dates = firm.daily_data.map(d => formatDate(d.date));
      const changeData = firm.daily_data.map(d => d.balance_change);
      const inflowData = firm.daily_data.map(d => d.inflow || 0);
      const outflowData = firm.daily_data.map(d => d.outflow || 0);

      // Check if this firm has any inflows over the threshold (only inflow triggers alerts)
      const threshold = firm.account_type === 'iolta' ? ioltaThreshold : checkingThreshold;
      const significantInflows = inflowData.filter(inflow => inflow >= threshold);

      if (significantInflows.length > 0) {
        const maxIncrease = changeData.length > 0 ? Math.max(...changeData) : 0;
        const maxIncreaseIndex = changeData.indexOf(maxIncrease);
        const maxIncreaseDate = maxIncreaseIndex >= 0 ? dates[maxIncreaseIndex] : 'N/A';
        const maxInflow = inflowData.length > 0 ? Math.max(...inflowData) : 0;
        const maxInflowIndex = inflowData.indexOf(maxInflow);
        const maxInflowDate = maxInflowIndex >= 0 ? dates[maxInflowIndex] : 'N/A';
        // For max outflow display, we want the largest absolute value
        const maxOutflowAbs = outflowData.length > 0 ? Math.max(...outflowData.map(Math.abs)) : 0;
        const maxOutflowIndex = outflowData.findIndex(val => Math.abs(val) === maxOutflowAbs);
        const maxOutflowDate = maxOutflowIndex >= 0 ? dates[maxOutflowIndex] : 'N/A';
        const daysWithHighInflow = inflowData.filter(inflow => inflow >= threshold).length;

        events.push({
          firmName,
          selectedAccount: firm.selected_account,
          totalAccounts: firm.total_accounts,
          accountType: firm.account_type,
          threshold: threshold,
          maxIncrease,
          maxIncreaseDate,
          maxIncreaseIndex,
          maxInflow,
          maxInflowDate,
          maxOutflow: maxOutflowAbs,
          maxOutflowDate,
          daysWithHighInflow,
          chartData: {
            labels: dates,
            datasets: [
              {
                label: 'Cash Flow',
                data: inflowData.map(v => v || 0),
                backgroundColor: dates.map((_, index) => {
                  const inflow = inflowData[index] || 0;
                  return inflow > 0 ? 'rgba(34, 197, 94, 0.8)' : 'transparent';
                }),
                borderColor: 'rgb(34, 197, 94)',
                borderWidth: 1,
                stack: 'stack1',
              },
              {
                label: 'Cash Flow Out',
                data: outflowData,
                backgroundColor: 'rgba(239, 68, 68, 0.8)', // Red for outflow
                borderColor: 'rgb(239, 68, 68)',
                borderWidth: 1,
                stack: 'stack1',
              }
            ]
          }
        });
      }
    });

    // Sort by maximum inflow (highest first)
    return events.sort((a, b) => b.maxInflow - a.maxInflow);
  };

  const barChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            const label = value >= 0 ? 'Inflow' : 'Outflow';
            const sign = value >= 0 ? '+' : '';
            return `${label}: ${sign}${formatCurrency(Math.abs(value))}`;
          }
        }
      }
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            const sign = value >= 0 ? '+' : '';
            return `${sign}${formatCurrency(value)}`;
          }
        }
      }
    },
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600">Loading balance analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start">
        <span>{error}</span>
      </div>
    );
  }

  const individualFirmCharts = getHighBalanceIncreaseEvents();

  return (
    <div className="space-y-5">
      {/* Controls */}
      <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters</span>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            {/* Days Selector */}
            <div className="flex items-center space-x-2">
              <label htmlFor="days" className="text-sm text-gray-600 whitespace-nowrap">
                Period:
              </label>
              <select
                id="days"
                value={selectedDays}
                onChange={(e) => setSelectedDays(parseInt(e.target.value))}
                className="border border-gray-300 rounded-xl bg-gray-50 px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
              </select>
            </div>

            {/* Account Type Checkboxes */}
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-600">Accounts:</span>
              <label className="flex items-center space-x-1">
                <input
                  type="checkbox"
                  checked={showIolta}
                  onChange={(e) => setShowIolta(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-600">IOLTA</span>
              </label>
              <label className="flex items-center space-x-1">
                <input
                  type="checkbox"
                  checked={showChecking}
                  onChange={(e) => setShowChecking(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-600">Checking</span>
              </label>
            </div>

            {/* IOLTA Threshold */}
            {showIolta && (
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600 whitespace-nowrap">IOLTA Threshold:</label>
                <select
                  value={useCustomIoltaThreshold ? 'custom' : ioltaThreshold}
                  onChange={(e) => handleIoltaThresholdChange(e.target.value)}
                  className="border border-gray-300 rounded-xl bg-gray-50 px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value={10000}>$10,000</option>
                  <option value={20000}>$20,000</option>
                  <option value={50000}>$50,000</option>
                  <option value={100000}>$100,000</option>
                  <option value="custom">Custom</option>
                </select>
                {useCustomIoltaThreshold && (
                  <div className="flex items-center space-x-1">
                    <span className="text-sm text-gray-600">$</span>
                    <input
                      type="number"
                      value={customIoltaThreshold}
                      onChange={(e) => setCustomIoltaThreshold(e.target.value)}
                      onBlur={applyCustomIoltaThreshold}
                      onKeyDown={(e) => { if (e.key === 'Enter') applyCustomIoltaThreshold(); }}
                      placeholder="Amount"
                      min="0"
                      step="1000"
                      className="border border-gray-300 rounded-xl bg-gray-50 px-2 py-1 text-sm w-28 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <button onClick={applyCustomIoltaThreshold} className="px-2 py-1 text-xs bg-brand-600 text-white rounded-full hover:bg-brand-700 transition-all duration-200">Apply</button>
                  </div>
                )}
              </div>
            )}

            {/* Checking Threshold */}
            {showChecking && (
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600 whitespace-nowrap">Checking Threshold:</label>
                <select
                  value={useCustomCheckingThreshold ? 'custom' : checkingThreshold}
                  onChange={(e) => handleCheckingThresholdChange(e.target.value)}
                  className="border border-gray-300 rounded-xl bg-gray-50 px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value={5000}>$5,000</option>
                  <option value={10000}>$10,000</option>
                  <option value={20000}>$20,000</option>
                  <option value={50000}>$50,000</option>
                  <option value={100000}>$100,000</option>
                  <option value="custom">Custom</option>
                </select>
                {useCustomCheckingThreshold && (
                  <div className="flex items-center space-x-1">
                    <span className="text-sm text-gray-600">$</span>
                    <input
                      type="number"
                      value={customCheckingThreshold}
                      onChange={(e) => setCustomCheckingThreshold(e.target.value)}
                      onBlur={applyCustomCheckingThreshold}
                      onKeyDown={(e) => { if (e.key === 'Enter') applyCustomCheckingThreshold(); }}
                      placeholder="Amount"
                      min="0"
                      step="1000"
                      className="border border-gray-300 rounded-xl bg-gray-50 px-2 py-1 text-sm w-28 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <button onClick={applyCustomCheckingThreshold} className="px-2 py-1 text-xs bg-brand-600 text-white rounded-full hover:bg-brand-700 transition-all duration-200">Apply</button>
                  </div>
                )}
              </div>
            )}

            {/* Email Subscriptions Button */}
            <button
              onClick={() => setShowSubscriptionModal(true)}
              className="flex items-center space-x-1 px-3 py-1 text-sm rounded-full bg-green-50 text-green-600 hover:bg-green-100 transition-all duration-200"
            >
              <Mail className="w-4 h-4" />
              <span>Email Alerts</span>
            </button>

            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`flex items-center space-x-1 px-3 py-1 text-sm rounded-full transition-all duration-200 ${
                refreshing
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
              }`}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>


      {/* Alert Summary */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-3xl p-5">
        <div className="flex items-center space-x-2 mb-2">
          <AlertTriangle className="w-5 h-5 text-yellow-600" />
          <h3 className="text-lg font-semibold text-yellow-800">Inflow Monitoring Alerts</h3>
        </div>
        <p className="text-sm text-yellow-700">
          Showing law firms with inflows exceeding threshold in the selected period.
          {showIolta && <span> IOLTA threshold: <span className="font-mono tabular-nums">{formatCurrency(ioltaThreshold)}</span>.</span>}
          {showChecking && <span> Checking threshold: <span className="font-mono tabular-nums">{formatCurrency(checkingThreshold)}</span>.</span>}
          {' '}Charts display both inflow (green) and outflow (red) for context.
          {individualFirmCharts && individualFirmCharts.length === 0 && (
            <span className="font-medium"> No alerts found - all inflows are below the threshold.</span>
          )}
        </p>
      </div>

      {/* Charts - Show only firms with significant balance increases */}
      {individualFirmCharts && individualFirmCharts.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {individualFirmCharts.map((firmChart) => (
            <div key={`${firmChart.firmName}-${firmChart.accountType}`} className="bg-white rounded-3xl shadow-md border border-red-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    {firmChart.firmName}
                  </h3>
                  <div className="text-sm text-gray-600 mt-1">
                    <span className="font-medium">{firmChart.selectedAccount.account_name}</span>
                    <span className={`ml-2 px-2.5 py-0.5 text-xs font-medium rounded-full ${
                      firmChart.accountType === 'iolta'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {firmChart.accountType === 'iolta' ? 'IOLTA' : 'Checking'}
                    </span>
                    <div className="text-xs text-gray-500 mt-1">
                      {firmChart.selectedAccount.customer_name} • Balance: <span className="font-mono tabular-nums">{formatCurrency(firmChart.selectedAccount.current_balance)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col space-y-1">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="w-4 h-4 text-green-500" />
                    <span className="text-xs font-medium text-green-600">
                      Max Inflow: <span className="font-mono tabular-nums">{formatCurrency(firmChart.maxInflow)}</span> on {firmChart.maxInflowDate}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <TrendingDown className="w-4 h-4 text-red-500" />
                    <span className="text-xs font-medium text-red-600">
                      Max Outflow: <span className="font-mono tabular-nums">{formatCurrency(firmChart.maxOutflow)}</span> on {firmChart.maxOutflowDate}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <BarChart3 className="w-4 h-4 text-gray-500" />
                    <span className="text-xs font-medium text-gray-600">
                      Net Change: <span className="font-mono tabular-nums">{formatCurrency(firmChart.maxIncrease)}</span> on {firmChart.maxIncreaseDate}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <div className="text-sm text-gray-600 text-center">
                  <span className="font-medium text-green-600">Alert Days: {firmChart.daysWithHighInflow}</span>
                  <span className="block text-xs mt-1">Inflow Threshold: <span className="font-mono tabular-nums">{formatCurrency(firmChart.threshold)}</span></span>
                </div>
              </div>
              <div className="h-64">
                <Bar data={firmChart.chartData} options={barChartOptions} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-green-50 rounded-3xl shadow-md border border-gray-100 p-8 text-center">
          <div className="flex justify-center mb-3">
            <TrendingUp className="w-12 h-12 text-green-500" />
          </div>
          <h3 className="text-lg font-semibold text-green-800 mb-2">No Inflow Alerts</h3>
          <p className="text-green-700">
            All law firms have inflows below threshold during this period.
          </p>
        </div>
      )}

      {/* Subscription Modal */}
      <SubscriptionModal 
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        onSubscriptionChange={fetchAnalyticsData}
      />
    </div>
  );
};

export default BalanceAnalytics;